import React, { useEffect, useState } from 'react';
import { AlertCircle, FileCheck, Send } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import { listBooks } from '../../../../../services/BooksService';
import { BillingClient, listBillingClients } from '../../../../../services/InvoicesService';
import { QUOTE_STATUSES, Quote, QuoteStatus, formatEuro } from '../masters/financeMasters';
import {
  DocumentLine,
  DocumentLinesEditor,
  FormSkeleton,
  InvoicePrefill,
  LineProduct,
  SearchSelect,
  addDays,
  formatDate,
  linesSubtotal,
  linesVat,
  newLine,
  today
} from './FormParts';
import '../../shared-modules.css';
import './FinanceForms.css';

const VALIDITY_DAYS = 30;

const STATUS_LABELS: Record<QuoteStatus, string> = {
  Pendiente: 'Pendiente',
  Aceptado: 'Aceptado',
  Rechazado: 'Rechazado'
};

interface QuoteFormProps {
  privacyMode: boolean;
  quote?: Quote;
  nextNumber: string;
  onSave: (quote: Quote) => void;
  onConvert: (prefill: InvoicePrefill) => void;
  onClose: () => void;
}

export const QuoteForm = ({ privacyMode, quote, nextNumber, onSave, onConvert, onClose }: QuoteFormProps) => {
  const { notify } = useToast();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [clients, setClients] = useState<BillingClient[]>([]);
  const [products, setProducts] = useState<LineProduct[]>([]);

  const number = quote ? quote.number : nextNumber;
  const [clientId, setClientId] = useState(quote ? quote.clientId : '');
  const [date, setDate] = useState(quote ? quote.date : today());
  const [validUntil, setValidUntil] = useState(quote ? quote.validUntil : addDays(today(), VALIDITY_DAYS));
  const [lines, setLines] = useState<DocumentLine[]>(quote && quote.lines.length > 0 ? quote.lines : [newLine()]);
  const [status, setStatus] = useState<QuoteStatus>(quote ? quote.status : 'Pendiente');
  const [notes, setNotes] = useState(quote ? quote.notes : '');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [clientList, books] = await Promise.all([listBillingClients(), listBooks()]);
        if (cancelled) return;
        setClients(clientList);
        setProducts(books.map(b => ({ id: b.id, name: b.title, sku: b.isbn || b.sku, price: b.price, vat: 4 })));
        setLoadError('');
      } catch (err) {
        console.error('Error loading quote form data:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar los clientes y artículos.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleDateChange = (value: string) => {
    setDate(value);
    if (value) setValidUntil(addDays(value, VALIDITY_DAYS));
  };

  const client = clients.find(c => c.id === clientId);
  const subtotal = linesSubtotal(lines);
  const vatTotal = linesVat(lines);
  const total = subtotal + vatTotal;

  const validate = () => {
    if (!client) return 'Seleccione un cliente.';
    if (!date || !validUntil) return 'La fecha y la validez son obligatorias.';
    if (validUntil < date) return 'La fecha de validez no puede ser anterior a la del presupuesto.';
    if (lines.some(l => !l.description.trim())) return 'Todas las líneas necesitan una descripción.';
    if (total <= 0) return 'El importe total del presupuesto debe ser mayor que cero.';
    return '';
  };

  const buildQuote = (patch: Partial<Quote> = {}): Quote => ({
    number,
    clientId,
    clientName: client.name,
    clientCif: client.cif,
    date,
    validUntil,
    lines,
    subtotal,
    vat: vatTotal,
    total,
    status,
    notes: notes.trim(),
    sentAt: quote ? quote.sentAt : '',
    ...patch
  });

  const handleAction = (action: 'save' | 'send' | 'convert') => {
    const error = validate() || (action === 'convert' && status === 'Rechazado' ? 'Un presupuesto rechazado no se puede convertir en factura.' : '');
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');

    if (action === 'convert') {
      onSave(buildQuote({ status: 'Aceptado' }));
      notify('Presupuesto aceptado', `Presupuesto ${number} pasado a factura. Revise los datos antes de emitirla.`, 'info');
      onConvert({ clientId, reference: number, lines, notes: notes.trim() });
      return;
    }

    if (action === 'send') {
      onSave(buildQuote({ sentAt: today() }));
      notify('Presupuesto enviado', `Presupuesto ${number} guardado y marcado como enviado el ${formatDate(today())}.`, 'info');
    } else {
      onSave(buildQuote());
      notify('Presupuesto guardado', `Presupuesto ${number} guardado.`, 'info');
    }
    onClose();
  };

  if (loading) return <FormSkeleton />;

  if (loadError) {
    return (
      <div className="module-panel">
        <div className="module-panel-empty">{loadError}</div>
      </div>
    );
  }

  const clientOptions = clients.map(c => ({ value: c.id, label: c.name, sub: c.cif ? `CIF: ${c.cif}` : undefined }));

  return (
    <div className="fin-doc">
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">VENTAS Y COBROS • PRESUPUESTOS</span>
          <h3 className="fin-doc__title"><FileCheck size={14} /> Presupuesto</h3>
        </div>
        <span className="fin-doc__badge">{status.toUpperCase()}</span>
      </div>

      {formError && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{formError}</span>
        </div>
      )}

      <div className="fin-doc__grid">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cliente *</label>
          <SearchSelect
            options={clientOptions}
            value={clientId}
            onChange={setClientId}
            placeholder={quote && !client ? `${quote.clientName} (elegir cliente)` : 'Buscar cliente por nombre o CIF...'}
            emptyText="No hay clientes"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Nº presupuesto autonumérico</label>
          <input
            type="text"
            readOnly
            className="sidebar-form-input fin-doc__mono fin-doc__readonly"
            value={quote ? number : `${number} (Automático)`}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Estado *</label>
          <select className="sidebar-form-input" value={status} onChange={(e) => setStatus(e.target.value as QuoteStatus)}>
            {QUOTE_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={date}
            onChange={(e) => handleDateChange(e.target.value)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Válido hasta *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </div>
      </div>

      <DocumentLinesEditor lines={lines} onChange={setLines} products={products} privacyMode={privacyMode} />

      <div className="fin-doc__totals">
        <span>Subtotal: <strong>{formatEuro(subtotal, privacyMode)}</strong></span>
        <span>IVA: <strong>{formatEuro(vatTotal, privacyMode)}</strong></span>
        <span className="fin-doc__total">Total presupuesto: <strong>{formatEuro(total, privacyMode)}</strong></span>
      </div>

      <div className="sidebar-form-group">
        <label className="sidebar-form-label">Notas</label>
        <textarea
          rows={3}
          className="sidebar-form-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Validez, descuentos por volumen, condiciones de entrega..."
        />
      </div>

      <div className="fin-doc__actions">
        <button type="button" className="fin-doc__btn" onClick={() => handleAction('save')}>
          Guardar
        </button>
        <button type="button" className="fin-doc__btn fin-doc__btn--outline" onClick={() => handleAction('convert')}>
          Convertir en factura
        </button>
        <button type="button" className="fin-doc__btn fin-doc__btn--primary" onClick={() => handleAction('send')}>
          <Send size={12} /> Enviar
        </button>
      </div>
    </div>
  );
};

export default QuoteForm;
